import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, streamRepoAsk, type AskCandidate } from "../lib/api";
import Markdown from "../components/Markdown";
import StreamProgress from "../components/StreamProgress";
import CitationChips from "../components/CitationChips";
import { repoDealLabel, repoDealRouteKey, type RepoMessage, type RepositoryDeal } from "@docreview/shared";

export default function RepositoryAskPage() {
  const [messages, setMessages] = useState<RepoMessage[]>([]);
  const [dealsById, setDealsById] = useState<Map<string, RepositoryDeal>>(new Map());
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [thinkingText, setThinkingText] = useState("");
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [candidates, setCandidates] = useState<AskCandidate[] | null>(null);
  const [findings, setFindings] = useState<Record<string, boolean>>({});
  const [err, setErr] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.listRepoAskMessages().then(({ messages }) => setMessages(messages));
    api.listRepoDeals("committed").then(({ deals }) => {
      setDealsById(new Map(deals.map((d) => [d.id, d])));
    });
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, streamText, status, candidates]);

  const resetProgress = () => {
    setStatus(null);
    setCandidates(null);
    setFindings({});
    setThinkingText("");
  };

  const send = async () => {
    const question = input.trim();
    if (!question || streaming) return;
    setInput("");
    setErr(null);
    setStreaming(true);
    setStreamText("");
    resetProgress();
    setStartedAt(Date.now());
    try {
      await streamRepoAsk(question, {
        onUserMessage: (m) => setMessages((prev) => [...prev, m]),
        onStatus: (t) => setStatus(t),
        onThinking: (t) => setThinkingText((prev) => prev + t),
        onCandidates: (c) => setCandidates(c.deals),
        onFinding: (f) => setFindings((prev) => ({ ...prev, [f.dealId]: f.applies })),
        onDelta: (t) => setStreamText((prev) => prev + t),
        onAssistantMessage: (m) => {
          setMessages((prev) => [...prev, m]);
          setStreamText("");
          resetProgress();
        },
        onError: (msg) => setErr(msg),
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setStreaming(false);
      setStreamText("");
      setStartedAt(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 flex flex-col h-[calc(100vh-3.5rem)]">
      <div className="flex items-center justify-between mb-1">
        <div>
          <Link to="/repository" className="text-sm text-sky-600 hover:underline">
            ← Repository
          </Link>
          <h1 className="text-lg font-semibold">Ask the repository</h1>
        </div>
        {messages.length > 0 && (
          <button
            className="text-xs text-slate-500 hover:text-red-600"
            disabled={streaming}
            onClick={async () => {
              if (!confirm("Clear the repository Q&A history for everyone?")) return;
              await api.clearRepoAskMessages();
              setMessages([]);
            }}
          >
            Clear history
          </button>
        )}
      </div>
      <p className="text-sm text-slate-500 mb-3">
        Ask across every committed deal — “which deals had A/B Notes with a DACA?”, “the 5 most
        recent convertible preferred deals”, “which deals allowed 3 deferments?”
      </p>

      <div ref={scrollRef} className="card flex-1 overflow-y-auto p-4 space-y-5">
        {messages.length === 0 && !streaming && (
          <div className="text-center text-sm text-slate-400 py-10">
            Ask a question to search the repository.
          </div>
        )}
        {messages.map((m) => (
          <AskBubble key={m.id} message={m} dealsById={dealsById} />
        ))}
        {streaming && (
          <ProgressView
            status={status}
            thinking={thinkingText}
            startedAt={startedAt}
            candidates={candidates}
            findings={findings}
            streamText={streamText}
          />
        )}
      </div>

      <div className="mt-3">
        {err && <div className="text-sm text-red-600 mb-2">{err}</div>}
        <div className="flex gap-2 items-end">
          <textarea
            className="input resize-none flex-1"
            rows={2}
            placeholder="Ask a question across all deals…"
            value={input}
            disabled={streaming}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
          />
          <button className="btn-primary" disabled={streaming || !input.trim()} onClick={send}>
            {streaming ? "…" : "Ask"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProgressView({
  status,
  thinking,
  startedAt,
  candidates,
  findings,
  streamText,
}: {
  status: string | null;
  thinking: string;
  startedAt: number | null;
  candidates: AskCandidate[] | null;
  findings: Record<string, boolean>;
  streamText: string;
}) {
  return (
    <div className="space-y-2">
      {!streamText && (
        <StreamProgress
          label={status}
          thinking={thinking}
          startedAt={startedAt}
          longRunningHint="Reading many deals takes a few minutes. Keep this tab open."
        />
      )}
      {candidates && candidates.length > 0 && !streamText && (
        <div className="flex flex-wrap gap-1.5">
          {candidates.map((c) => {
            const applies = findings[c.id];
            return (
              <span
                key={c.id}
                className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-mono ${
                  applies === undefined
                    ? "bg-slate-100 text-slate-500"
                    : applies
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-slate-100 text-slate-400 line-through"
                }`}
              >
                {applies === undefined ? "•" : applies ? "✓" : "✕"} {c.label}
              </span>
            );
          })}
        </div>
      )}
      {streamText && (
        <div>
          <div className="text-xs text-slate-500 mb-1">Answer</div>
          <Markdown>{streamText}</Markdown>
        </div>
      )}
    </div>
  );
}

function AskBubble({
  message: m,
  dealsById,
}: {
  message: RepoMessage;
  dealsById: Map<string, RepositoryDeal>;
}) {
  const isUser = m.role === "user";
  const cited = useMemo(
    () => (m.citedDealIds ?? []).map((id) => ({ id, deal: dealsById.get(id) })),
    [m.citedDealIds, dealsById],
  );
  // Deals already represented by a passage citation — don't also list them as
  // bare deal chips. The rest (applied but no deep-link) still get a chip.
  const citedDocDeals = useMemo(
    () => new Set((m.citations ?? []).map((c) => c.dealId).filter(Boolean) as string[]),
    [m.citations],
  );
  const uncitedDeals = useMemo(
    () => cited.filter(({ id }) => !citedDocDeals.has(id)),
    [cited, citedDocDeals],
  );
  if (isUser) {
    return (
      <div className="text-right">
        <div className="text-xs text-slate-500 mb-1">{m.createdByName ?? m.createdByEmail ?? "You"}</div>
        <div className="inline-block text-left rounded-lg px-3 py-2 bg-ink text-white">
          <span className="whitespace-pre-wrap text-sm">{m.content}</span>
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className="text-xs text-slate-500 mb-1">Repository</div>
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
        <Markdown>{m.content || (m.error ?? "(empty)")}</Markdown>
        {/* Passage-level chips → open the doc + highlight (deals with a quote). */}
        {m.citations && m.citations.length > 0 && <CitationChips citations={m.citations} />}
        {/* Deal-level chips → open the deal page. Shows applied deals that have
            no passage citation (and all sources for metadata-only answers), so
            no source is ever dropped. */}
        {uncitedDeals.length > 0 && (
          <div className="mt-2 pt-2 border-t border-slate-200 flex flex-wrap gap-1.5">
            <span className="text-[11px] text-slate-400 mr-1">
              {m.citations && m.citations.length > 0 ? "Other deals:" : "Sources:"}
            </span>
            {uncitedDeals.map(({ id, deal }) => (
              <Link
                key={id}
                // Prefer the friendly label URL; the doc id still resolves for
                // any deal not loaded into the map.
                to={`/repository/deals/${deal ? repoDealRouteKey({ ...deal, id }) : id}`}
                className="inline-block rounded bg-white border border-slate-200 px-1.5 py-0.5 text-[11px] font-mono hover:border-slate-400"
              >
                {deal ? repoDealLabel(deal.ticker, deal.dealNumber) : id.slice(0, 6)}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
